import * as React from "react";
import {cx} from "emotion";

import "./big-svg-line-steps.scss";

import {cs}              from "@Common/react/chain-services";
import {keyed}           from "@Common/react/keyed";
import {Static}          from "@Common/react/static";
import {UseState}        from "@Common/react/use-state";
import {DomSizeObserver} from "@Common/ui-components/dom-size-observer";
import {randomId}        from "@Common/utils/random";
import {waitTimeout}     from "@Common/utils/wait-timeout";

import {polygonToSvgCurvePath} from "./polygon-to-svg-curve-path";

export const BigSvgLineSteps = ({next}) => cs(
    ["glowers", (_, next) => Static({initValue: [], next})],
    ({glowers}) => next({
        glowStep: (i) => {
            glowers[i]();
        },
        render: ({steps}) => (
            <div className="big-svg-line-steps-0tu">
                {steps.map((step, i) => cs(
                    keyed(i),
                    ["size", ({}, next) => DomSizeObserver({defaultValue:null, next})],
                    ({size}) => (
                        <div {...{
                            className: cx(
                                "step",
                                {
                                    active   : step.active,
                                    disabled : step.disabled,
                                    completed: step.completed,
                                    hasError : step.hasError,
                                },
                            ),
                            ref: size.ref,
                            onClick: () => !step.disabled && step.onClick(),

                            "xd-qa-type": "button",
                            "xd-qa-name": "Wizard Step" + " " + step.label + (step.active ? " Active" : ""),
                        }}>
                            {size.value && cs(
                                ["glow", (_, next) => UseState({next})],
                                ({glow}, next) => {
                                    glowers[i] = async () => {
                                        await animationFrames((delta) => {
                                            glow.onChange(delta / 300);
                                        }, 300);
                                        await animationFrames((delta) => {
                                            glow.onChange((300 - delta) / 300);
                                        }, 300);
                                        await waitTimeout(500);
                                        await animationFrames((delta) => {
                                            glow.onChange(delta / 300);
                                        }, 300);
                                        await animationFrames((delta) => {
                                            glow.onChange((300 - delta) / 300);
                                        }, 300);
                                    };
                                    return next();
                                },
                                ({glow}) => (
                                    BigStepSvg({
                                        size: size.value,
                                        backward: i > 0,
                                        forward: i < steps.length - 1,
                                        glow: glow.value,
                                    })
                                ),
                            )}

                            <div className="center">
                                <div className="icon">
                                    <i {...{
                                        className: "fa fa-" + step.icon,

                                        "xd-qa-type": "icon",
                                        "xd-qa-name": step.label,
                                    }}/>
                                    {(step.completed || step.hasError) && (
                                        <div {...{
                                            className: "status",
                                            "xd-qa-type": "icon",
                                            "xd-qa-name": (
                                                step.completed ? "Completed" : "Error"
                                            ),
                                        }}>
                                            {step.completed ? (
                                                <i className="fa fa-check-circle"/>
                                            ) : (
                                                <i className="fa fa-exclamation-triangle"/>
                                            )}
                                        </div>
                                    )}
                                </div>


                                <div className="label">
                                    {step.label}
                                </div>
                            </div>
                        </div>
                    )
                ))}
            </div>
        ),
    }),
);

const BigStepSvg = ({size, forward, backward, glow}) => {
    const arrowCurveSize = 8;
    return cs(
        ["glowId", (_, next) => Static({getInitValue: randomId, next})],
        ({glowId}) => (
            <svg
                width={size.width + 40}
                height={size.height + 20}
            >
                <defs>
                    <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur in="SourceGraphic" stdDeviation="3" />
                        <feComponentTransfer>
                            <feFuncA type="linear" slope={!glow ? 0 : 2 * glow} />
                        </feComponentTransfer>
                        <feMerge>
                            <feMergeNode />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                <g {...{
                    transform: "translate(20, 10)",
                    ...glow && {
                        filter: `url(#${glowId})`,
                    },
                }}>
                    <path
                        d={polygonToSvgCurvePath([
                            ...backward ? [
                                {x: - arrowCurveSize, y: size.height},
                                {x:   arrowCurveSize, y: size.height / 2, noCurve: true},
                                {x: - arrowCurveSize, y: 0},
                            ] : [
                                {x: 0, y: size.height},
                                {x: 0, y: 0},
                            ],

                            ...forward ? [
                                {x: size.width - arrowCurveSize, y: 0},
                                {x: size.width + arrowCurveSize, y: size.height / 2, noCurve: true},
                                {x: size.width - arrowCurveSize, y: size.height},
                            ] : [
                                {x: size.width, y: 0},
                                {x: size.width, y: size.height},
                            ],
                        ], {radius: 3})}
                    />
                </g>
            </svg>
        ),
    );
};

const animationFrames = (fn, duration) => new Promise<void>((resolve) => {
    const start = Date.now();
    const frame = () => {
        const time = Date.now() - start;
        fn(time);
        if (time < duration) {
            requestAnimationFrame(frame);
        } else {
            resolve();
        }
    };
    requestAnimationFrame(frame);
});
